import React, { useState } from "react";

// ---------------------------------------------------------------------------
// Gated Horizon Cases — public sponsor-facing method demonstration.
//
// Six gates only. Mobile-first. No organisation-specific opportunity data,
// partner names, feedstock details, evidence gaps, commercial assumptions or
// sign-off decisions appear on this public page. Real opportunity work sits
// on a separate permissioned, noindex route.
//
// No KATLAS hashes, signatures, signer references, attestations or node
// timestamps are fabricated anywhere in this component.
// ---------------------------------------------------------------------------

const CASES = [
  {
    id: "cluster-5-battery",
    title: "Cluster 5 — Battery Materials Gateway",
    subtitle: "From sustainable material idea to governed Horizon fit",
    description:
      "An illustrative gated case showing how a candidate battery-materials opportunity could be assessed for Horizon partner discussion. The journey tests source logic, evidence readiness, partner gaps, measurable benefit and accountable sign-off. No real opportunity data is shown here.",
    enabled: true,
  },
  {
    id: "cluster-6-farm-to-fork",
    title: "Cluster 6 — Farm-to-Fork / Bioeconomy Gateway",
    subtitle: "Coming next",
    enabled: false,
  },
  {
    id: "smart-cities",
    title: "Smart Cities / Public Services Gateway",
    subtitle: "Coming next",
    enabled: false,
  },
  {
    id: "life-sciences",
    title: "Life Sciences / ISPE DIN Gateway",
    subtitle: "Coming next",
    enabled: false,
  },
];

// ---------- Source cards used inside Gate 2 (Horizon logic) ----------

const HORIZON_SOURCE_CARDS = [
  {
    id: "batt4eu",
    title: "Horizon / BATT4EU topic",
    tells:
      "The programme objective — what Horizon Europe is asking applicants to deliver.",
    evidence:
      "Fit to sustainable processing / refining or bio-based electrode materials.",
  },
  {
    id: "eu-batteries",
    title: "EU battery sustainability regulation",
    tells:
      "The regulatory pressure shaping how battery materials must perform across their lifecycle.",
    evidence:
      "Lifecycle, safety, carbon, circularity or material-responsibility evidence.",
  },
  {
    id: "crm-autonomy",
    title: "Critical raw materials / strategic autonomy agenda",
    tells: "The market and resilience pressure behind the call.",
    evidence:
      "Reduced dependency, resilient processing or European value-chain relevance.",
  },
];

// ---------- The six sponsor-facing gates ----------

const BATTERY_GATES = [
  {
    n: "01",
    title: "Opportunity",
    prompt:
      "Capture the material, process or feedstock idea — the candidate opportunity in plain language.",
    captures: [
      "Working name and one-paragraph description",
      "Type of opportunity (e.g. sustainable processing, bio-based material, triage)",
      "Material or feedstock category",
      "Estimated maturity / TRL band",
      "Lead organisation and relationship owner (held privately)",
    ],
    sample: {
      label: "Synthetic sample opportunity",
      name: "Bio-based carbon material from agricultural residue",
      note: "Generic illustrative example only. Not a real opportunity, partner or feedstock relationship.",
    },
  },
  {
    n: "02",
    title: "Horizon logic",
    prompt:
      "Why does this Horizon topic exist? What policy, regulatory or market pressure created the call, and what must the opportunity evidence to be credible?",
    horizonLogic: true,
    captures: [
      "Reduces dependency on imported battery-grade raw materials",
      "Supports European value chains or strategic autonomy",
      "Relates to sustainable processing / refining or bio-based electrode materials",
      "Plausible route to LCA / LCC or SSbD-style assessment",
      "Plausible route to TRL 4–5 validation",
    ],
    capturesLabel: "What the opportunity must evidence",
    note: "This is preliminary opportunity analysis. It is not legal advice, scientific validation or official Horizon eligibility. Interpretation may be stewarded by the lead organisation alongside legal / regulatory, LCA / SSbD, materials-science or industrial partners as the case develops.",
  },
  {
    n: "03",
    title: "Evidence & custody",
    prompt:
      "Map the evidence categories. Each is held by a role, not centralised. Status records readiness, not the content of the evidence.",
    evidenceCategories: [
      "Feedstock / source description",
      "Process description",
      "Material characterisation",
      "LCA / LCC evidence",
      "SSbD / safety evidence",
      "Industrial validation pathway",
      "Exploitation / offtake pathway",
    ],
    legend: [
      { label: "Held by source role", tone: "blue",  description: "Custody stays with the party that holds it" },
      { label: "Shared (refs only)",   tone: "blue",  description: "Refs, hashes and metadata — never raw private data" },
      { label: "Withheld",             tone: "slate", description: "Held in custody, deliberately not shared" },
      { label: "Available under NDA",  tone: "slate", description: "Conditional share to a defined counterparty" },
      { label: "Missing",              tone: "amber", description: "Not yet available — flagged as a gap" },
    ],
    note: "KATLAS records the governed action, not a copy of everyone's private data.",
  },
  {
    n: "04",
    title: "Partner gaps",
    prompt:
      "List the roles a credible consortium would need. The gap pattern is what informs the next decision.",
    roles: [
      "Materials science / RTO",
      "Battery cell validation partner",
      "Sustainable processing / refining partner",
      "LCA / LCC partner",
      "SSbD / safety partner",
      "Industrial end-user or offtaker",
      "SME or feedstock holder",
      "Regional or development sponsor",
      "Exploitation and commercialisation partner",
    ],
    legend: ["Present", "Possible", "Missing", "Not required"],
  },
  {
    n: "05",
    title: "Measurable benefit",
    prompt:
      "Force discipline on the claim. Avoid vague sustainability language unless tied to an evidence category.",
    captures: [
      "Main sponsor objective the opportunity serves",
      "Measurable benefit and who benefits",
      "Why now — timing or window driver",
      "Possible budget / funding route",
      "What would make this credible within 30 days",
      "What would make this credible within 90 days",
    ],
  },
  {
    n: "06",
    title: "Sponsor sign-off and receipt",
    prompt:
      "A visible decision moment, followed by a structured receipt. The lead organisation commits to one outcome with reason, evidence relied upon and next action.",
    decisions: [
      "Proceed to partner discussion",
      "Hold pending evidence",
      "Reject / not current fit",
      "Convert into partner-search brief",
      "Convert into Horizon concept note",
    ],
    captures: [
      "Decision and decision-maker role (named individuals held privately)",
      "Date and time",
      "Reason in plain language",
      "Evidence relied upon (refs)",
      "Evidence withheld or missing (counts and categories)",
      "Next action and next review date",
    ],
    receipt: {
      fieldNames: [
        "Opportunity ID",
        "Decision",
        "Decision-maker role",
        "Date / time",
        "Horizon fit status",
        "Evidence status",
        "Partner gap status",
        "Next action",
      ],
      buckets: [
        { title: "Shared evidence",   tone: "blue",  description: "Categories whose refs / hashes are recorded on the receipt." },
        { title: "Withheld evidence", tone: "slate", description: "Categories deliberately held in custody and noted by count only." },
        { title: "Missing evidence",  tone: "amber", description: "Categories flagged as gaps in the next-action plan." },
      ],
      label:
        "Local opportunity receipt — KATLAS signing not enabled in this version.",
      note: "When KATLAS live signing is enabled for an organisation's private route, the same shape becomes a KATLAS-signed receipt with hash, signer reference, attestation reference, node timestamp and verification status. Those fields are never fabricated here.",
    },
    disclaimer:
      "This is an opportunity qualification decision, not scientific validation, legal advice, regulatory approval or formal Horizon eligibility confirmation. Real decisions are recorded against private opportunity records, not shown on this public page.",
  },
];

// Top-of-section journey strip
const JOURNEY = [
  "Source logic",
  "Evidence",
  "Partner gap",
  "Benefit",
  "Sign-off",
  "Receipt",
];

// Intro shown above the Cluster 5 gates — keep tight on mobile.
const BATTERY_INTRO = {
  title: "From Horizon topic to governed delivery case",
  paragraphs: [
    "Most Horizon-fit tools begin by asking whether an idea matches the wording of a call. This gateway starts earlier and goes further.",
    "It asks why the call exists: which regulations, strategic agendas, market pressures and evidence requirements created the opportunity in the first place. A sponsor lead can then test whether a battery-materials idea genuinely answers that source logic, rather than merely sounding relevant.",
  ],
  noveltyLabel: "The novelty is the governed journey",
  helps: {
    label: "This helps a sponsor see",
    items: [
      "why the opportunity matters",
      "what must be evidenced",
      "who holds the relevant evidence",
      "what remains private or missing",
      "which partners are needed",
      "what measurable benefit is plausible",
      "who is accountable for the next decision",
    ],
  },
  closing: [
    "KATLAS provides the governance frame underneath: custody of evidence, authority at the point of action, and receipts that prove what was decided, by whom, using which evidence and with what gaps.",
    "This first gated case is deliberately narrow. It uses an illustrative Cluster 5 battery-materials opportunity to show how a sponsor lead can turn a promising lead into a disciplined, sponsor-readable qualification journey before committing time, introductions or consortium effort.",
  ],
  benefits: [
    {
      title: "Not keyword matching",
      body: "Tests the policy and regulatory source logic behind the call.",
    },
    {
      title: "Sponsor-readable",
      body: "Shows why the opportunity matters and what decision is being requested.",
    },
    {
      title: "Evidence-aware",
      body: "Separates shared, withheld and missing evidence without forcing full disclosure.",
    },
    {
      title: "Accountable next step",
      body: "Makes the sponsor lead's proceed / hold / reject decision visible and receipted.",
    },
  ],
};

// ---------------------------------------------------------------------------
// shared UI atoms
// ---------------------------------------------------------------------------

const toneClasses = {
  blue:  "text-[#0066FF] border-[#0066FF]/30 bg-[#0066FF]/[0.06]",
  amber: "text-[#EAB308] border-amber-500/30 bg-amber-500/[0.06]",
  slate: "text-slate-200 border-white/15 bg-white/[0.03]",
};

const GateShell = ({ n, title, prompt, children, accent = "blue" }) => (
  <article
    data-testid={`gate-card-${n}`}
    className="bg-[#020B14] border border-white/[0.08] p-5 sm:p-6"
  >
    <div className="flex items-baseline gap-3 mb-2">
      <span
        className={`mono text-[10px] uppercase tracking-[0.22em] ${
          accent === "amber" ? "text-[#EAB308]" : "text-[#0066FF]"
        }`}
      >
        GATE {n}
      </span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
    <h4 className="font-display text-[16px] sm:text-lg text-white leading-snug">
      {title}
    </h4>
    {prompt && (
      <p className="mt-2 text-sm text-slate-300 leading-relaxed">{prompt}</p>
    )}
    {children}
  </article>
);

const CaptureList = ({ items, label = "What this gate captures" }) => (
  <div className="mt-4 border-l-2 border-[#0066FF]/40 pl-3">
    <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
      {label}
    </div>
    <ul className="mt-2 space-y-1.5 text-sm text-slate-200 leading-relaxed">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2">
          <span className="mt-2 w-1 h-1 bg-[#0066FF]" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Note = ({ text }) => (
  <p className="mt-4 text-xs text-slate-500 italic leading-relaxed">{text}</p>
);

// ---------------------------------------------------------------------------
// gate-specific renderers
// ---------------------------------------------------------------------------

const WhyThisCallExists = () => (
  <section
    data-testid="why-this-call-exists"
    className="mt-4 border border-[#0066FF]/25 bg-[#0066FF]/[0.05] p-4 sm:p-5"
  >
    <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
      Why this call exists
    </div>
    <p className="mt-2 text-sm text-slate-200 leading-relaxed">
      This opportunity is not assessed by keyword matching alone. The source
      logic behind the Horizon topic is tested first: the regulations,
      strategic agendas and market pressures that explain why the funding
      exists. That helps a sponsor see whether the idea answers the real
      programme need, not just the wording of the call.
    </p>
  </section>
);

const SourceCards = () => (
  <div
    data-testid="source-cards"
    className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
  >
    {HORIZON_SOURCE_CARDS.map((s) => (
      <article
        key={s.id}
        data-testid={`source-card-${s.id}`}
        className="bg-white/[0.02] border border-white/[0.1] p-4"
      >
        <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
          Initiating source
        </div>
        <h5 className="font-display text-[15px] text-white mt-2 leading-snug">
          {s.title}
        </h5>
        <div className="mt-3 border-l-2 border-[#0066FF]/40 pl-3 space-y-2.5">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.18em] text-[#0066FF]">
              What it tells us
            </div>
            <p className="mt-1 text-xs text-slate-300 leading-relaxed">{s.tells}</p>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.18em] text-[#EAB308]">
              What must be evidenced
            </div>
            <p className="mt-1 text-xs text-slate-300 leading-relaxed">
              {s.evidence}
            </p>
          </div>
        </div>
      </article>
    ))}
  </div>
);

const HorizonLogicBody = ({ captures, capturesLabel, note }) => (
  <>
    <WhyThisCallExists />
    <SourceCards />
    <CaptureList items={captures} label={capturesLabel} />
    <Note text={note} />
  </>
);

const EvidenceLegend = ({ categories, legend, note }) => (
  <>
    <div className="mt-4 border border-white/[0.08]">
      <div className="px-4 py-3 border-b border-white/[0.08] mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
        Evidence categories
      </div>
      <ul className="divide-y divide-white/[0.06]">
        {categories.map((cat) => (
          <li
            key={cat}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200"
          >
            <span className="w-1 h-1 bg-[#0066FF]" />
            {cat}
          </li>
        ))}
      </ul>
    </div>
    <div className="mt-4 grid grid-cols-1 gap-2">
      {legend.map((l) => (
        <div
          key={l.label}
          className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border ${toneClasses[l.tone]} px-3 py-2`}
        >
          <span className="mono text-[10px] uppercase tracking-[0.18em] min-w-[160px]">
            {l.label}
          </span>
          <span className="text-xs text-slate-300">{l.description}</span>
        </div>
      ))}
    </div>
    {note && <Note text={note} />}
  </>
);

const RoleList = ({ roles, legend }) => (
  <>
    <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
      {roles.map((r) => (
        <li
          key={r}
          className="flex items-center justify-between gap-3 border border-white/15 text-slate-100 px-3 py-2"
        >
          <span className="text-sm">{r}</span>
        </li>
      ))}
    </ul>
    <div className="mt-3 flex flex-wrap gap-2">
      {legend.map((l) => (
        <span
          key={l}
          className="mono text-[10px] uppercase tracking-[0.18em] border border-white/15 text-slate-400 px-2.5 py-1"
        >
          {l}
        </span>
      ))}
    </div>
  </>
);

const ReceiptShape = ({ receipt }) => (
  <>
    <div className="mt-6 mono text-[10px] uppercase tracking-[0.22em] text-[#EAB308]">
      Receipt — shape only
    </div>
    <div
      data-testid="receipt-card"
      className="mt-2 border border-white/[0.12] bg-white/[0.02]"
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
        <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
          Local opportunity receipt · shape only
        </span>
        <span
          data-testid="receipt-kind"
          className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500 border border-white/15 px-2 py-0.5"
        >
          KATLAS signing not enabled
        </span>
      </header>
      <ul className="p-4 grid grid-cols-1 gap-1.5 text-sm">
        {receipt.fieldNames.map((k) => (
          <li
            key={k}
            className="flex flex-wrap items-baseline gap-2 border-b border-white/[0.04] pb-2 last:border-b-0"
          >
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500 min-w-[150px]">
              {k}
            </span>
            <span className="text-slate-500 italic">populated on sign-off</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
      {receipt.buckets.map((b) => (
        <div
          key={b.title}
          data-testid={`receipt-bucket-${b.tone}`}
          className="bg-[#020B14] border border-white/[0.08] p-4"
        >
          <div
            className={`mono text-[10px] uppercase tracking-[0.22em] ${
              b.tone === "amber"
                ? "text-[#EAB308]"
                : b.tone === "blue"
                ? "text-[#0066FF]"
                : "text-slate-300"
            }`}
          >
            {b.title}
          </div>
          <p className="mt-2.5 text-xs text-slate-400 leading-relaxed">
            {b.description}
          </p>
        </div>
      ))}
    </div>

    <p
      data-testid="receipt-label"
      className="mt-4 mono text-[10px] uppercase tracking-[0.18em] text-slate-500"
    >
      {receipt.label}
    </p>
    <Note text={receipt.note} />
  </>
);

const SignOffAndReceipt = ({ decisions, captures, receipt, disclaimer }) => (
  <>
    <div className="mt-4">
      <div className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
        Decision options
      </div>
      <div className="flex flex-wrap gap-2">
        {decisions.map((d) => (
          <span
            key={d}
            className="mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border border-white/15 text-slate-300"
          >
            {d}
          </span>
        ))}
      </div>
    </div>
    <CaptureList items={captures} label="What the sign-off records" />
    <ReceiptShape receipt={receipt} />
    <Note text={disclaimer} />
  </>
);

// ---------------------------------------------------------------------------
// journey strip
// ---------------------------------------------------------------------------

const JourneyStrip = () => (
  <div
    data-testid="journey-strip"
    className="mt-6 -mx-1 px-1 overflow-x-auto no-scrollbar"
  >
    <ol className="flex items-center gap-2 min-w-max">
      {JOURNEY.map((step, i) => (
        <React.Fragment key={step}>
          <li className="flex items-center gap-2">
            <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="text-sm text-white font-display">{step}</span>
          </li>
          {i < JOURNEY.length - 1 && (
            <li aria-hidden className="text-[#0066FF]" data-testid="journey-arrow">
              <svg
                width="14"
                height="10"
                viewBox="0 0 14 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
              >
                <path d="M0 5h12M9 1l4 4-4 4" />
              </svg>
            </li>
          )}
        </React.Fragment>
      ))}
    </ol>
  </div>
);

// ---------------------------------------------------------------------------
// Cluster-5 intro panel (novelty + benefits)
// ---------------------------------------------------------------------------

const BatteryCaseIntro = () => (
  <section
    data-testid="case-intro"
    className="mt-7 border border-white/[0.1] bg-[#0B1B30] p-5 sm:p-6"
  >
    <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
      Introduction · novelty &amp; benefits
    </div>
    <h4
      data-testid="case-intro-title"
      className="font-display text-[18px] sm:text-2xl text-white mt-2 leading-snug"
    >
      {BATTERY_INTRO.title}
    </h4>

    {BATTERY_INTRO.paragraphs.map((p, i) => (
      <p
        key={i}
        className="mt-3 text-sm sm:text-[15px] text-slate-300 leading-relaxed"
      >
        {p}
      </p>
    ))}

    <div className="mt-5">
      <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
        {BATTERY_INTRO.noveltyLabel}
      </div>
      <p
        data-testid="case-intro-journey"
        className="mt-2 font-display text-base sm:text-lg text-white leading-snug"
      >
        Source logic <span className="text-slate-500">&rarr;</span> Evidence{" "}
        <span className="text-slate-500">&rarr;</span> Partner gap{" "}
        <span className="text-slate-500">&rarr;</span> Benefit{" "}
        <span className="text-slate-500">&rarr;</span> Sign-off{" "}
        <span className="text-slate-500">&rarr;</span>{" "}
        <span className="text-[#EAB308]">Receipt</span>
      </p>
    </div>

    <div className="mt-5">
      <div className="mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        {BATTERY_INTRO.helps.label}
      </div>
      <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5 text-sm text-slate-200 leading-relaxed">
        {BATTERY_INTRO.helps.items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <span className="mt-2 w-1 h-1 bg-[#0066FF]" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>

    {BATTERY_INTRO.closing.map((p, i) => (
      <p key={i} className="mt-4 text-sm text-slate-300 leading-relaxed">
        {p}
      </p>
    ))}

    <div
      data-testid="case-intro-benefits"
      className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5"
    >
      {BATTERY_INTRO.benefits.map((b, i) => (
        <div
          key={b.title}
          data-testid={`benefit-chip-${i + 1}`}
          className="border border-white/[0.12] bg-[#020B14] p-3.5"
        >
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[#0066FF]" />
            <span className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              0{i + 1}
            </span>
          </div>
          <div className="font-display text-[14px] sm:text-[15px] text-white mt-1.5 leading-snug">
            {b.title}
          </div>
          <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">
            {b.body}
          </p>
        </div>
      ))}
    </div>
  </section>
);


// ---------------------------------------------------------------------------
// component
// ---------------------------------------------------------------------------

export default function GatedHorizonCases() {
  const [selectedId, setSelectedId] = useState("cluster-5-battery");
  const selected = CASES.find((c) => c.id === selectedId);
  const gates = selected?.id === "cluster-5-battery" ? BATTERY_GATES : [];

  return (
    <section
      id="gated-horizon-cases"
      data-testid="section-gated-cases"
      className="mt-14 border-t border-white/[0.08] pt-10"
    >
      <header>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
          Gated Horizon Cases · method only
        </div>
        <h2 className="font-display text-[22px] sm:text-3xl mt-3 leading-tight">
          KATLAS turns Horizon topics into governed{" "}
          <span className="text-slate-400">opportunity journeys.</span>
        </h2>

        <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
          Machine-readable Horizon topics tell participants what the programme
          is asking for. KATLAS adds the governed operating layer: who may
          act, who holds the evidence, what can be shared, what remains
          private, and what receipt proves the qualification decision.
        </p>

        <p
          data-testid="public-method-only-notice"
          className="mt-3 text-xs text-slate-500 italic leading-relaxed"
        >
          This page demonstrates the method. Organisation-specific opportunity
          data, partner names and sign-off decisions are held in private,
          permissioned routes — not shown here.
        </p>

        <JourneyStrip />
      </header>

      {/* Case selector */}
      <div className="mt-7">
        <label
          htmlFor="case-select"
          className="block mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2"
        >
          Choose a gated case
        </label>
        <div className="relative">
          <select
            id="case-select"
            data-testid="case-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full appearance-none bg-[#071627] border border-white/[0.15] hover:border-white/30 focus:border-[#0066FF] outline-none text-white px-4 py-3.5 pr-12 text-sm font-medium transition-colors"
          >
            {CASES.map((c) => (
              <option key={c.id} value={c.id} disabled={!c.enabled}>
                {c.title}
                {!c.enabled ? "  ·  coming next" : ""}
              </option>
            ))}
          </select>
          <svg
            className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </div>

      {/* Case header */}
      {selected && (
        <div className="mt-7">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
            Selected case
          </div>
          <h3
            data-testid="case-title"
            className="font-display text-xl sm:text-2xl mt-2 text-white"
          >
            {selected.title}
          </h3>
          <p className="text-sm sm:text-base text-slate-400 mt-1">
            {selected.subtitle}
          </p>
          {selected.description && (
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">
              {selected.description}
            </p>
          )}

          {/* Synthetic-case notice */}
          <div
            data-testid="synthetic-notice"
            className="mt-4 border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3"
          >
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[#EAB308]">
              Synthetic example
            </div>
            <p className="mt-1.5 text-sm text-amber-100 leading-relaxed">
              Illustrative synthetic case. No confidential partner, sponsor,
              feedstock, evidence or opportunity data is shown.
            </p>
          </div>
        </div>
      )}

      {/* Cluster-5 intro panel (novelty + benefits) */}
      {selectedId === "cluster-5-battery" && <BatteryCaseIntro />}

      {/* Gates — six only */}
      <div data-testid="gates-list" className="mt-7 grid grid-cols-1 gap-3.5">
        {gates.map((g) => {
          const accent = g.n === "06" ? "amber" : "blue";
          return (
            <GateShell
              key={g.n}
              n={g.n}
              title={g.title}
              prompt={g.prompt}
              accent={accent}
            >
              {g.horizonLogic && (
                <HorizonLogicBody
                  captures={g.captures}
                  capturesLabel={g.capturesLabel}
                  note={g.note}
                />
              )}
              {g.captures && !g.horizonLogic && !g.decisions && (
                <CaptureList items={g.captures} />
              )}
              {g.sample && !g.decisions && (
                <div
                  data-testid={`gate-${g.n}-sample`}
                  className="mt-4 border border-amber-500/25 bg-amber-500/[0.04] p-4"
                >
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#EAB308]">
                    {g.sample.label}
                  </div>
                  <p className="mt-2 font-display text-base text-white leading-snug">
                    {g.sample.name}
                  </p>
                  <p className="mt-1.5 text-xs text-slate-400 italic leading-relaxed">
                    {g.sample.note}
                  </p>
                </div>
              )}
              {g.evidenceCategories && (
                <EvidenceLegend
                  categories={g.evidenceCategories}
                  legend={g.legend}
                  note={g.note}
                />
              )}
              {g.roles && <RoleList roles={g.roles} legend={g.legend} />}
              {g.decisions && (
                <SignOffAndReceipt
                  decisions={g.decisions}
                  captures={g.captures}
                  receipt={g.receipt}
                  disclaimer={g.disclaimer}
                />
              )}
            </GateShell>
          );
        })}
      </div>

      {/* Public-method disclaimer */}
      <div className="mt-8 border border-white/[0.08] bg-white/[0.02] p-4 sm:p-5">
        <p className="text-xs text-slate-400 leading-relaxed">
          The Gated Horizon Cases section is an illustrative two-minute
          explainer of the qualification method. It is not scientific
          validation, legal advice, regulatory approval or formal Horizon
          eligibility confirmation. It does not contain any organisation&rsquo;s
          real opportunity data, partner names, feedstock specifics,
          commercial assumptions or sign-off decisions. KATLAS hashes,
          signatures, signer references, attestations and node timestamps are{" "}
          <em className="not-italic text-slate-200">not</em> fabricated on
          this page — any production receipt would come from a live KATLAS
          node, against a private opportunity record on a permissioned route.
        </p>
      </div>
    </section>
  );
}
